import { fetchJSON as fetchAPI } from './client';

export type OrchestrationDashboard = {
  tenant_id: string;
  workspace_id?: string;
  active_workflows: number;
  running_executions: number;
  failed_executions: number;
  pending_approvals: number;
  open_tasks: number;
  pending_approval_tasks: number;
  events_processed_24h: number;
  active_agents: number;
  scheduled_jobs: number;
  ai_cost_today: number;
  avg_execution_ms: number;
  unread_notifications: number;
  recent_activity?: ActivityEntry[];
  generated_at: string;
};

export type ActivityEntry = {
  id: string;
  tenant_id: string;
  resource_id?: string;
  category: string;
  action: string;
  message: string;
  actor?: string;
  metadata?: Record<string, unknown>;
  created_at: string;
};

export type OrchestrationNotification = {
  id: string;
  tenant_id: string;
  resource_id?: string;
  title: string;
  body?: string;
  priority: string;
  severity?: string;
  read: boolean;
  acknowledged: boolean;
  action_url?: string;
  created_at: string;
};

export type OrchestrationEvent = {
  id: string;
  tenant_id: string;
  workspace_id: string;
  source: string;
  event_type: string;
  payload?: Record<string, unknown>;
  processed: boolean;
  routed_to?: string[];
  received_at: string;
};

export type OrchestrationTask = {
  id: string;
  tenant_id: string;
  workspace_id: string;
  agent_type?: string;
  title: string;
  business_reason: string;
  evidence?: string[];
  suggested_action: string;
  estimated_impact?: number;
  requires_approval: boolean;
  priority: string;
  status: string;
  owner_id?: string;
  due_date?: string;
  release_package_id?: string;
  created_at: string;
  updated_at: string;
};

export type AgentDefinition = {
  id: string;
  tenant_id: string;
  workspace_id: string;
  type: string;
  name: string;
  description?: string;
  capabilities?: string[];
  requires_approval: boolean;
  active: boolean;
  created_at: string;
};

export type AutomationRule = {
  id: string;
  tenant_id: string;
  workspace_id: string;
  name: string;
  domain: string;
  condition_expr: string;
  then_action: string;
  then_workflow?: string;
  then_notify?: string;
  severity?: string;
  active: boolean;
  created_at: string;
};

export type SchedulerJob = {
  id: string;
  tenant_id: string;
  workspace_id: string;
  name: string;
  frequency: string;
  cron_expr?: string;
  timezone?: string;
  workflow_id?: string;
  rule_id?: string;
  active: boolean;
  last_run_at?: string;
  next_run_at?: string;
  created_at: string;
};

export type ApprovalRequest = {
  id: string;
  tenant_id: string;
  execution_id: string;
  node_id: string;
  status: string;
  reviewer_id?: string;
  comment?: string;
  expires_at?: string;
  created_at: string;
  updated_at: string;
};

export type WorkflowExecution = {
  id: string;
  tenant_id: string;
  workspace_id: string;
  workflow_id: string;
  workflow_version: number;
  status: string;
  current_node?: string;
  error?: string;
  started_at: string;
  updated_at: string;
  completed_at?: string;
};

const API_BASE = import.meta.env.VITE_WORKFLOW_API ?? '/api/v1/workflow';

function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const workflowApi = {
  dashboard: (workspaceId?: string) =>
    fetchJSON<OrchestrationDashboard>(`/dashboard${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),

  ingestEvent: (body: {
    workspace_id: string;
    source: string;
    event_type: string;
    payload?: Record<string, unknown>;
  }) => fetchJSON<OrchestrationEvent>('/events', { method: 'POST', body: JSON.stringify(body) }),

  processEvent: (id: string) => fetchJSON<OrchestrationEvent>(`/events/${id}/process`, { method: 'POST' }),

  listTasks: (workspaceId?: string) =>
    fetchJSON<OrchestrationTask[]>(`/tasks${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),

  createTask: (body: {
    workspace_id: string;
    agent_type?: string;
    title: string;
    business_reason: string;
    suggested_action: string;
    estimated_impact?: number;
    requires_approval?: boolean;
    priority?: string;
    evidence?: string[];
  }) => fetchJSON<OrchestrationTask>('/tasks', { method: 'POST', body: JSON.stringify(body) }),

  transitionTask: (id: string, body: { status: string; actor?: string; comment?: string }) =>
    fetchJSON<OrchestrationTask>(`/tasks/${id}/transition`, { method: 'POST', body: JSON.stringify(body) }),

  listAutomationRules: (workspaceId?: string) =>
    fetchJSON<AutomationRule[]>(`/automation/rules${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),

  createAutomationRule: (body: {
    workspace_id: string;
    name: string;
    domain: string;
    condition_expr: string;
    then_action: string;
    then_workflow?: string;
    then_notify?: string;
    severity?: string;
  }) => fetchJSON<AutomationRule>('/automation/rules', { method: 'POST', body: JSON.stringify(body) }),

  listAgents: (workspaceId?: string) =>
    fetchJSON<AgentDefinition[]>(`/agents${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),

  listSchedulerJobs: (workspaceId?: string) =>
    fetchJSON<SchedulerJob[]>(`/scheduler/jobs${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),

  createSchedulerJob: (body: {
    workspace_id: string;
    name: string;
    frequency: string;
    cron_expr?: string;
    timezone?: string;
    workflow_id?: string;
    rule_id?: string;
  }) => fetchJSON<SchedulerJob>('/scheduler/jobs', { method: 'POST', body: JSON.stringify(body) }),

  approvalCenter: (workspaceId?: string) =>
    fetchJSON<ApprovalRequest[]>(`/approvals/center${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),

  executionMonitor: (workspaceId?: string) =>
    fetchJSON<WorkflowExecution[]>(`/executions/monitor${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),

  listActivity: (limit = 20) => fetchJSON<ActivityEntry[]>(`/activity?limit=${limit}`),

  listNotifications: (unread = true) => fetchJSON<OrchestrationNotification[]>(`/notifications?unread=${unread}`),

  markNotificationRead: (id: string) =>
    fetchJSON<{ status: string }>(`/notifications/${id}/read`, { method: 'POST' }),

  adminHealth: () => fetchJSON<Record<string, unknown>>('/admin/health'),
};
